import { formatCopy } from '@/i18n/ns';
import type { BillingCopyKey } from '@/i18n/ns/billing';
import type { BillingStatus, CodeCheck, PlanOption, PlanTiming } from './api';
import {
  addMonths,
  credits,
  currentPlanIndex,
  discounted,
  effectiveDiscount,
  balanceCredits,
  kr,
  perCredit,
  percentText,
  shortDate,
  vatMode,
  withVat,
  type EffectiveDiscount,
  type VatMode,
} from './plan-math';

export type BillingCopy = Readonly<Record<BillingCopyKey, string>>;

interface Inputs {
  status: BillingStatus;
  plans: PlanOption[];
  index: number;
  fresh: CodeCheck | null;
  recentCredits: number | null;
  locale: string;
  copy: BillingCopy;
  now?: Date;
}

/** The text of the code chip: how long a fresh code lasts, or when the active one ends. */
export function discountText(
  discount: EffectiveDiscount,
  copy: BillingCopy,
  locale: string,
  carries = false,
): string {
  const percent = percentText(discount.percentOff, locale);
  if (discount.fresh) {
    if (discount.months == null) return formatCopy(copy.codeForGood, { percent });
    if (discount.months === 1) return formatCopy(copy.codeForOneMonth, { percent });
    return formatCopy(copy.codeForMonths, { percent, months: discount.months });
  }
  if (!discount.endsAt)
    return formatCopy(carries ? copy.codeCarriesForGood : copy.codeForGood, { percent });
  return formatCopy(carries ? copy.codeCarries : copy.codeUntil, {
    percent,
    date: shortDate(discount.endsAt, locale),
  });
}

/** When a discount that starts on `start` ends, if it ends at all. */
export function discountEnd(discount: EffectiveDiscount, start: Date): Date | null {
  if (!discount.fresh) return discount.endsAt ? new Date(discount.endsAt) : null;
  return discount.months == null ? null : addMonths(start, discount.months);
}

function priceNote(discount: EffectiveDiscount | null, copy: BillingCopy, locale: string) {
  if (!discount) return copy.exclVat;
  if (discount.fresh) {
    if (discount.months == null) return copy.exclVat;
    return discount.months === 1
      ? copy.exclVatForOneMonth
      : formatCopy(copy.exclVatForMonths, { months: discount.months });
  }
  return discount.endsAt
    ? formatCopy(copy.exclVatUntil, { date: shortDate(discount.endsAt, locale) })
    : copy.exclVat;
}

function caption(input: Inputs, current: number, plan: PlanOption) {
  const { copy, locale, recentCredits } = input;
  const recent =
    recentCredits == null || recentCredits <= 0
      ? ''
      : formatCopy(copy.captionRecent, { count: credits(recentCredits, locale) });
  if (current < 0) return recent;
  if (current === input.index) return [copy.captionCurrent, recent].filter(Boolean).join(' ');
  const diff = plan.credits - input.plans[current].credits;
  const first = formatCopy(diff > 0 ? copy.captionMore : copy.captionFewer, {
    count: credits(Math.abs(diff), locale),
  });
  return [first, recent].filter(Boolean).join(' ');
}

function kicker(copy: BillingCopy, current: number, index: number) {
  if (current < 0 || current === index) return '';
  return index > current ? copy.kickerUpgrade : copy.kickerDowngrade;
}

/** Everything step one shows for the selected plan. */
export function planStepView(input: Inputs) {
  const { status, plans, index, copy, locale } = input;
  const plan = plans[index];
  const account = status.account;
  const current = currentPlanIndex(plans, account);
  const mode = vatMode(account);
  const discount = effectiveDiscount(account, input.fresh);
  const price = discounted(plan.priceSek, discount);
  return {
    plan,
    current,
    isCurrent: current === index,
    hasPlan: current >= 0,
    mode,
    discount,
    kicker: kicker(copy, current, index),
    credits: credits(plan.credits, locale),
    caption: caption(input, current, plan),
    listPrice: kr(plan.priceSek, locale),
    price: kr(price, locale),
    priceNote: priceNote(discount, copy, locale),
    perCredit: perCredit(plan.orePerCredit, locale),
    volume: plan.volumeDiscountPercent > 0 ? `${plan.volumeDiscountPercent}%` : copy.volumeNone,
    volumeNote:
      plan.volumeDiscountPercent > 0
        ? formatCopy(copy.volumeNote, { price: perCredit(plans[0].orePerCredit, locale) })
        : copy.volumeBase,
    listVat: kr(withVat(plan.priceSek, mode), locale),
    vat: kr(withVat(price, mode), locale),
    sliderValue: formatCopy(copy.sliderValue, {
      credits: credits(plan.credits, locale),
      price: kr(price, locale),
    }),
    endLow: formatCopy(copy.scaleEnd, { price: kr(plans[0].priceSek, locale) }),
    endHigh: formatCopy(copy.scaleEnd, { price: kr(plans.at(-1)!.priceSek, locale) }),
  };
}

export const footNote = (mode: VatMode, copy: BillingCopy) =>
  mode === 'se' ? copy.footSe : mode === 'reverse' ? copy.footReverse : copy.footUnknown;

/** Step two: what happens today and afterwards for each timing. */
export function timingView(input: Inputs & { timing: PlanTiming }) {
  const step = planStepView(input);
  const { copy, locale, status } = input;
  const now = input.now ?? new Date();
  const renewal = status.account.periodEnd ? new Date(status.account.periodEnd) : null;
  const renewalLabel = renewal ? shortDate(renewal, locale) : '';
  const paid = withVat(discounted(step.plan.priceSek, step.discount), step.mode);
  const full = withVat(step.plan.priceSek, step.mode);
  const balance = balanceCredits(status.account.creditBalanceMicros);
  const percent = percentText(step.discount?.percentOff ?? 0, locale);
  const endNow = step.discount ? discountEnd(step.discount, now) : null;
  const endRenewal = step.discount && renewal ? discountEnd(step.discount, renewal) : null;
  const planCredits = formatCopy(copy.creditsCount, { count: step.credits });
  const value = {
    price: kr(paid, locale),
    fullPrice: kr(full, locale),
    credits: planCredits,
    balance: credits(balance, locale),
    total: credits(balance + step.plan.credits, locale),
    codeNote: step.discount ? ` ${formatCopy(copy.codeNote, { percent })}` : '',
  };
  const currentPlan = step.current >= 0 ? input.plans[step.current] : null;
  return {
    step,
    renewalLabel,
    value,
    summaryFrom: currentPlan
      ? formatCopy(copy.creditsCount, { count: credits(currentPlan.credits, locale) })
      : '',
    summaryTo: formatCopy(copy.planPerMonth, { credits: step.credits }),
    summaryPrice: [
      formatCopy(step.mode === 'reverse' ? copy.summaryExclVat : copy.summaryInclVat, {
        price: value.price,
      }),
      step.discount ? formatCopy(copy.summaryOff, { percent }) : '',
    ]
      .filter(Boolean)
      .join(', '),
    nowToday: balance > 0 ? copy.nowTodayOnTop : copy.nowToday,
    nowThen: endNow ? copy.nowThenUntil : copy.nowThen,
    nowThenDate: endNow ? shortDate(endNow, locale) : '',
    renewalToday: status.canAsk ? copy.renewalToday : copy.renewalTodayPaused,
    renewalCodeNote:
      step.discount && endRenewal
        ? ` ${formatCopy(copy.codeNoteUntil, { percent, date: shortDate(endRenewal, locale) })}`
        : value.codeNote,
    foot: footNote(step.mode, copy),
    confirm:
      input.timing === 'now'
        ? formatCopy(copy.confirmNow, { price: value.price })
        : formatCopy(copy.confirmRenewal, { credits: step.credits }),
  };
}
